import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { makeAuthenticatedGETRequest } from "../../services/serverHelper";
import { patronProfilePoints } from "../../services/apis";
import Patron_Navbar from "../Patron_Navbar";
import PatronSidebar from "../PatronSidebar";
import "./ViewPerformances.css";

const formatDate = (dateValue) => {
  if (!dateValue) return "N/A";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const resolveImageUrl = (performance) => {
  return (
    performance?.image ||
    performance?.imageUrl ||
    performance?.images?.[0] ||
    performance?.performanceImage ||
    ""
  );
};

const resolveVideoUrl = (performance) => {
  return (
    performance?.videoUrl ||
    performance?.video ||
    performance?.performanceVideo ||
    ""
  );
};

const ViewPerformances = () => {
  const { accessToken } = useSelector((state) => state.auth);
  const [performances, setPerformances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchPerformances = async () => {
      setLoading(true);
      setError("");

      if (!accessToken) {
        setError("Authentication required to load performances.");
        setLoading(false);
        return;
      }

      try {
        const response = await makeAuthenticatedGETRequest(
          patronProfilePoints.GET_USER_PERFORMANCES_API,
          accessToken
        );

        if (response?.statusCode === 200) {
          const payload = response.data ?? response.performances ?? [];
          console.log("Fetched performances:", payload);
          setPerformances(Array.isArray(payload) ? payload : []);
        } else {
          setError(
            response?.message || response?.error || "Unable to load performances."
          );
        }
      } catch (fetchError) {
        setError(
          fetchError?.message || "Unable to load performances. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    const getFullImageUrl = (imageValue) => {
      if (!imageValue) return "/default-avatar.png";
      let raw = String(imageValue).replace(/[\u0000-\u001F\u007F]+/g, "").trim();
      if (!raw) return "/default-avatar.png";
      if (raw.startsWith("blob:")) return raw;
      if (/^https?:\/\//i.test(raw)) return raw;
      if (raw.startsWith("//")) return "https:" + raw;
      let cleaned = raw
        .replace(/^\/+/, "")
        .replace(/^public\/images\//i, "")
        .replace(/^public\/uploads\//i, "")
        .replace(/^uploads\/performance\//i, "")
        .replace(/^uploads\//i, "")
        .replace(/^images\//i, "")
        .replace(/^api\/v1\/images\//i, "")
        .replace(/^api\/images\//i, "")
        .replace(/^https?:\/\/api\.ekalakaar\.com\/api\/v1\/images\//i, "")
        .replace(/^https?:\/\/api\.ekalakaar\.com\/uploads\/performance\//i, "")
        .replace(/^https?:\/\/api\.ekalakaar\.com\//i, "")
        .trim();
      const envBase = process.env.REACT_APP_BASE_URL || "https://api.ekalakaar.com";
      const API_HOST = String(envBase).replace(/\/api(\/v?1)?(\/.*)?$/i, "").replace(/\/+$/, "");
      return `${API_HOST}/images/${cleaned}`;
    };  

  useEffect(() => {

    fetchPerformances();
  }, [accessToken]);

    const handleViewPerformance = (performance) => {
      // For now, just alerting the performance details. You can replace this with a modal or a new page.
    }

  return (
    <>
      <Patron_Navbar />
      <div className="dashboard-layout">
        <PatronSidebar />
        <div className="dashboard-content">
          <section className="performances_page">
            <div className="performances_header">
              <div className="performances_header_content">
                <h1>PERFORMANCES</h1>
              </div>
            </div>

            {loading ? (
              <div className="performances_loading">
                <div className="performances_spinner" />
                <p>Loading performances...</p>
              </div>
            ) : error ? (
              <div className="performances_error">
                <span>⚠️</span>
                <div>{error}</div>
              </div>
            ) : performances.length === 0 ? (
              <div className="performances_empty">
                <h2>No performances available</h2>
                <p>When you have performance records, they will appear here.</p>
              </div>
            ) : (
              <div className="performances_table_wrapper">
                <table className="performances_table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Opportunity Title</th>
                      <th>Summary</th>
                      <th>Notes</th>
                      <th>Image</th>
                      <th>Video</th>
                      <th>Reviews Count</th>
                      <th>Created Date</th>
                      {/* <th>Action</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {performances.map((performance, index) => {
                      const imageUrl = getFullImageUrl(performance.imageUploadLink);
                      const videoUrl = resolveVideoUrl(performance);
                      const reviewsCount =
                        performance?.review?.length ??
                        0;
                      return (
                        <tr
                          key={performance?._id ?? `${index}`}
                          className={index % 2 === 0 ? "row_even" : "row_odd"}
                        >
                          <td>{formatDate(performance?.performanceDate ?? performance?.date)}</td>
                          <td>{performance?.opportunityId?.purpose ?? "—"}</td>
                          <td>{performance?.summary ?? performance?.performanceSummary ?? "—"}</td>
                          <td>{performance?.notes ?? performance?.remarks ?? "—"}</td>
                          <td className="performances_image_cell">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt="Performance preview"
                                className="performance_thumbnail"
                              />
                            ) : (
                              <span className="performance_placeholder">No Image</span>
                            )}
                          </td>
                          <td>
                            {videoUrl ? (
                              <a
                                href={videoUrl}
                                className="performance_link"
                                target="_blank"
                                rel="noreferrer"
                              >
                                View Video
                              </a>
                            ) : (
                              <span className="performance_placeholder">No Video</span>
                            )}
                          </td>
                          <td>{reviewsCount}</td>
                          <td>{formatDate(performance?.createdAt)}</td>
                          {/* <td className="actions_cell">
                            <div className="actions_group">
                              <button type="button" onClick={() => handleViewPerformance(performance)} className="action_button view_btn">
                                View
                              </button>
                            </div>
                          </td> */}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
};

export default ViewPerformances;
